import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Label } from "@sablier/v2-components/molecules";
import { Input } from "@sablier/v2-components/organisms";
import { tags } from "@sablier/v2-constants";
import { useDebounce } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Token } from "@sablier/v2-models";
import { IToken } from "@sablier/v2-types";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { useModalTokenPicker, useTokenSearch, useTokens } from "~/client/hooks";
import { Empty, Item, Loader } from "../common";
import { ItemAction } from "../common/Item";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: ${(props) => props.theme.sizes.edge};
    padding-bottom: 0;
  }
`;

const Search = styled.div`
  width: 100%;
  margin-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
`;

const Results = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
  }
`;

const ListWrapper = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
    height: 100%;
    padding-top: 4px;
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
    ${Search} {
      margin-bottom: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
  }
`;
interface ItemData {
  action: ItemAction;
  list: IToken[];
  onClick: (item: IToken) => void;
}

interface Props {
  onChoose: (token: IToken) => void;
  onRegistry: () => void;
}

function TokenPickerHome({ onChoose, onRegistry }: Props) {
  const { t } = useT();
  const [search, setSearch] = useState<string>();
  const virtualListRef = useRef<HTMLDivElement>();
  const { data: modalData } = useModalTokenPicker();
  const { isLoading, list: raw } = useTokens();

  const debounced = useDebounce(search, 100);
  const sorted = useTokenSearch(raw, debounced);
  const count = useMemo(() => sorted.length, [sorted]);

  const filtered = useMemo(() => {
    if (!modalData || !modalData.chosen) {
      return sorted;
    }
    const { chosen } = modalData;

    return sorted.map((token) => {
      if (Token.areEqual(token, chosen)) {
        const clone = _.clone(token);
        clone.isSelected = true;
        return clone;
      }
      token.isSelected = false;
      return token;
    });
  }, [modalData, sorted]);

  const onItemClick = useCallback(
    (item: IToken) => {
      onChoose(item);
    },
    [onChoose],
  );

  const itemData: ItemData = useMemo(
    () => ({
      action: ItemAction.CHOOSE,
      list: filtered,
      onClick: onItemClick,
    }),
    [filtered, onItemClick],
  );

  useEffect(() => {
    if (virtualListRef?.current) {
      virtualListRef.current.scrollTop = 0;
    }
  }, [debounced]);

  return (
    <Wrapper data-cy={tags.modalTokenPicker}>
      {isLoading ? (
        <Content>
          <Loader />
        </Content>
      ) : (
        <Content>
          <Search>
            <Input.TextSearch
              id={"tokenPickerSearch"}
              onChange={(value) => {
                setSearch(value ?? "");
              }}
              onClear={() => {
                setSearch("");
              }}
              placeholder={t("form.placeholder.textSearch")}
              value={search}
            />
          </Search>

          <Results>
            <Label
              value={`${t("structs.allTokens")}${count ? ` (${count})` : ""}`}
            />
            <ListWrapper>
              {count === 0 && <Empty isSaveSuggested onSuggest={onRegistry} />}
              <AutoSizer>
                {({ height, width }: { width: number; height: number }) => (
                  <List<ItemData>
                    height={height ?? 0}
                    itemCount={count}
                    itemData={itemData}
                    itemSize={60}
                    outerRef={virtualListRef}
                    width={width ?? 0}
                  >
                    {Item}
                  </List>
                )}
              </AutoSizer>
            </ListWrapper>
          </Results>
        </Content>
      )}
    </Wrapper>
  );
}

export default TokenPickerHome;
